/**
 * Auth-aware contractor-profile API.
 *
 * Signed-in users hit `/api/profile` (auth required, keyed server-side by
 * user_id). Anonymous users read/write `sessionStorage["ledger.profile"]` —
 * per-tab, ephemeral, zero MongoDB writes.
 *
 * On first sign-in `ProfileMigrator` reads sessionStorage and POSTs it to
 * the backend if the user has no existing profile, then clears sessionStorage.
 */
import { useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useApiClient } from "./client";

const SESSION_KEY = "ledger.profile";

export function readSessionProfile() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeSessionProfile(profile) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(profile));
  } catch {
    /* private mode / quota — ignore */
  }
}

export function clearSessionProfile() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function useProfileApi() {
  const { isSignedIn } = useAuth();
  const api = useApiClient();

  const fetchProfile = useCallback(async () => {
    if (isSignedIn) {
      try {
        const r = await api.get("/profile");
        return r.data;
      } catch (err) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    }
    return readSessionProfile();
  }, [api, isSignedIn]);

  const saveProfile = useCallback(
    async (profile) => {
      if (isSignedIn) {
        const r = await api.put("/profile", profile);
        return r.data;
      }
      writeSessionProfile(profile);
      return profile;
    },
    [api, isSignedIn],
  );

  // Logo upload + delete. Signed-in only — the backend requires auth, and
  // we don't base64-stash binary blobs in sessionStorage (5MB cap is too
  // tight a margin to ship reliably). Anonymous users see a "sign in to
  // upload a logo" message in the Settings BrandSection.
  const uploadLogo = useCallback(
    async (file) => {
      if (!isSignedIn) {
        throw new Error("Sign in to upload a logo.");
      }
      const fd = new FormData();
      fd.append("file", file);
      // Let the browser set the multipart boundary by not pinning the
      // Content-Type header explicitly. Axios picks it up from the
      // FormData instance.
      const r = await api.post("/profile/logo", fd);
      return r.data;
    },
    [api, isSignedIn],
  );

  const deleteLogo = useCallback(async () => {
    if (!isSignedIn) {
      throw new Error("Sign in to remove the logo.");
    }
    const r = await api.delete("/profile/logo");
    return r.data;
  }, [api, isSignedIn]);

  return { fetchProfile, saveProfile, uploadLogo, deleteLogo };
}
